import axios, { baseUrl } from "../axios/axios";
import {
  PharmacyLicenseResponse,
  PharmacyLicenseCreateRequest,
  PharmacyLicenseUpdateRequest,
} from "../types/pharmacyLicense";

const LICENSE_ENDPOINT = "/marketplace_pharmacy_license";
const LICENSE_IMAGE_PATH = "marketplace_pharmacy_license_images";

/**
 * Get all pharmacy licenses for a marketplace
 */
export const getPharmacyLicenses = async (
  branchMarketplaceId: number,
  marketplaceId: number
): Promise<PharmacyLicenseResponse[]> => {
  const response = await axios.get(
    `${baseUrl}${LICENSE_ENDPOINT}/branch_marketplace_id/${branchMarketplaceId}/marketplace_id/${marketplaceId}`
  );
  return response.data;
};

/**
 * Create a new pharmacy license
 * @param request - License data
 * @param images - Array of File objects for license images
 */
export const createPharmacyLicense = async (
  request: PharmacyLicenseCreateRequest,
  images: File[]
): Promise<void> => {
  const formData = new FormData();

  // Note: Backend expects "marketplacePharmacyLicenseRequestString" as the key
  formData.append(
    "marketplacePharmacyLicenseRequestString",
    JSON.stringify(request)
  );

  // Append all image files
  images.forEach((image) => {
    formData.append("images", image);
  });

  await axios.post(`${baseUrl}${LICENSE_ENDPOINT}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

/**
 * Update an existing pharmacy license
 * @param request - License data with id
 * @param images - Array of File objects for license images
 * Note: Backend deletes all existing images, so send ALL images (existing + new)
 */
export const updatePharmacyLicense = async (
  request: PharmacyLicenseUpdateRequest,
  images: File[]
): Promise<void> => {
  const formData = new FormData();

  formData.append(
    "marketplacePharmacyLicenseRequestString",
    JSON.stringify(request)
  );

  images.forEach((image) => {
    formData.append("images", image);
  });

  await axios.put(`${baseUrl}${LICENSE_ENDPOINT}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

/**
 * Delete a pharmacy license (soft delete)
 */
export const deletePharmacyLicense = async (
  licenseId: number
): Promise<void> => {
  await axios.delete(`${baseUrl}${LICENSE_ENDPOINT}/${licenseId}`);
};

/**
 * Set the active pharmacist license for the marketplace.
 * This is called when user selects a pharmacist from the dropdown.
 *
 * Note: This endpoint also sets takesOrders=1 as a side effect,
 * which is harmless since the pharmacy should be active when selecting.
 *
 * @param branchMarketplaceId - Branch marketplace ID
 * @param marketplaceId - Marketplace ID
 * @param licenseId - License ID to set as active, or null to clear
 */
export const setActivePharmacyLicense = async (
  branchMarketplaceId: number,
  marketplaceId: number,
  licenseId: number | null
): Promise<void> => {
  const params =
    licenseId !== null ? `?marketplacePharmacyLicenseId=${licenseId}` : "";

  await axios.put(
    `${baseUrl}/marketplace/${branchMarketplaceId}/${marketplaceId}${params}`
  );
};

/**
 * Helper to get full image URL from imageLink
 */
export const getLicenseImageUrl = (imageLink: string): string => {
  const cdnPath = process.env.REACT_APP_CDN_PATH || "";
  return `${cdnPath}${LICENSE_IMAGE_PATH}/${imageLink}`;
};

/**
 * Helper to format license for dropdown display
 */
export const formatLicenseLabel = (
  license: PharmacyLicenseResponse
): string => {
  return `${license.licenseUserFirstName} ${license.licenseUserSecondName} (${license.licenseCode})`;
};
